import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Global keyboard chord handler. Mounted once in Layout (returns null).
 *
 * Chords (g + letter) navigate to a top-level section. A single key opens
 * Cmd+K-like actions but for navigation; the chord-leader resets after 1500ms
 * to avoid the user being trapped in a "g..." mode forever.
 *
 * Skipped when the user is typing in any input/textarea/contenteditable.
 *
 * Listed here = listed in ShortcutHelp.js. Keep them in sync.
 */
const GO_MAP = {
  h: '/',
  p: '/procurement/purchase-orders',
  i: '/inventory',
  r: '/retail/orders',
  f: '/finance/reconciliation',
  a: '/operations',
};

export default function GlobalShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();
  const chordLeader = useRef(null);
  const chordTimer = useRef(null);

  useEffect(() => {
    const isEditable = (target) => {
      if (!target) return false;
      if (target.isContentEditable) return true;
      const tag = String(target.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select';
    };

    const clearChord = () => {
      chordLeader.current = null;
      if (chordTimer.current) clearTimeout(chordTimer.current);
      chordTimer.current = null;
    };

    const onKey = (e) => {
      if (isEditable(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return; // these belong to other handlers

      // 1) Chord mode: a `g` press primes the leader; the next single letter resolves.
      if (chordLeader.current === 'g') {
        const target = GO_MAP[e.key.toLowerCase()];
        clearChord();
        if (target) {
          e.preventDefault();
          if (location.pathname !== target) navigate(target);
        }
        return;
      }
      if (e.key.toLowerCase() === 'g') {
        chordLeader.current = 'g';
        chordTimer.current = setTimeout(clearChord, 1500);
        return;
      }

      // 2) Single-letter shortcuts on list pages.
      //    Dispatched as a custom event so individual list components opt in.
      //    Currently consumed by GlobalShortcuts → no-op here; pages can add
      //    `useListShortcuts({ onNew, onSearch, onRefresh })`.
      if (e.key === 'n' || e.key === '/' || e.key === 'r') {
        const evt = new CustomEvent('app:list-shortcut', { detail: { key: e.key } });
        // Prevent default only for `/` so the browser quickfind doesn't fire.
        if (e.key === '/') e.preventDefault();
        window.dispatchEvent(evt);
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearChord();
    };
  }, [navigate, location.pathname]);

  return null;
}

/**
 * Hook for list pages to register `n` / `/` / `r` shortcuts. The handler can
 * be omitted — that shortcut is then a no-op for the current page.
 */
export function useListShortcuts({ onNew, onSearch, onRefresh } = {}) {
  useEffect(() => {
    const handler = (e) => {
      const key = e.detail?.key;
      if (key === 'n' && onNew) onNew();
      else if (key === '/' && onSearch) onSearch();
      else if (key === 'r' && onRefresh) onRefresh();
    };
    window.addEventListener('app:list-shortcut', handler);
    return () => window.removeEventListener('app:list-shortcut', handler);
  }, [onNew, onSearch, onRefresh]);
}
